/**
 * Hand-authored inline SVG icons (Story 2.1, judgement call 3).
 *
 * `DESIGN.md`/`EXPERIENCE.md` require icons beside nav labels but name no
 * library, and none is in the Stack table — adding one for nine glyphs would
 * be a spine change this story doesn't need to make. Every icon here is a
 * plain 20×20 line glyph on `currentColor`, so it inherits whatever text
 * color its wrapping element sets (nav item ink, active accent, and so on)
 * without a component-level color prop. The wrapping control supplies the
 * 44×44 tap target (DESIGN.md § Layout & Spacing: "icon-only buttons get an
 * explicit 44×44 box even when the glyph is 20px") — the glyph's own
 * dimensions are not a spacing-scale value and are intentionally a bare
 * number, not a themed token.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 9.75 10 4l6.5 5.75" />
      <path d="M5.25 8.25V16.5h9.5V8.25" />
    </Icon>
  );
}

export function PropertiesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="3.25" width="9" height="13.5" rx="0.75" />
      <path d="M13 8.25h2.75v8.5H13" />
      <path d="M6.5 6.5h2M6.5 9.5h2M6.5 12.5h2" />
    </Icon>
  );
}

export function TenantsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="7.5" cy="7" r="2.25" />
      <path d="M3.25 16c0-2.4 1.9-4 4.25-4s4.25 1.6 4.25 4" />
      <circle cx="14" cy="6.5" r="1.75" />
      <path d="M12.5 9.25c1.85.35 3.25 1.7 3.25 3.75" />
    </Icon>
  );
}

export function EquipmentIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.3 4.3a3 3 0 0 0-4 3.6L4 12.2v2.3h2.3l4.3-4.3a3 3 0 0 0 3.6-4l-1.9 1.9-1.6-.4-.4-1.6 1.9-1.9Z" />
    </Icon>
  );
}

export function BillsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5.25 3.5h9.5v13l-1.6-1.1-1.6 1.1-1.6-1.1-1.6 1.1-1.6-1.1-1.5 1.1Z" />
      <path d="M7.25 7h5.5M7.25 9.75h5.5M7.25 12.5h3.5" />
    </Icon>
  );
}

export function MoneyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.25" y="5.5" width="13.5" height="9.5" rx="1.25" />
      <path d="M3.25 8.5h13.5" />
      <circle cx="13.25" cy="11.75" r="1.35" />
    </Icon>
  );
}

export function WorkOrdersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="4" width="11" height="13" rx="1" />
      <path d="M7.5 3.75h5v1.75h-5Z" />
      <path d="M7 10.5l1.75 1.75 3.25-3.75" />
    </Icon>
  );
}

export function DocumentsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3.5h5.5L15 7v9.5H6Z" />
      <path d="M11.25 3.5V7H15" />
      <path d="M7.75 10.25h4.5M7.75 12.75h4.5" />
    </Icon>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="6.25" cy="6.5" r="1.4" />
      <path d="M3.5 6.5h1.4M8.6 6.5h7.9" />
      <circle cx="13.5" cy="10.5" r="1.4" />
      <path d="M3.5 10.5h8.15M14.85 10.5h1.65" />
      <circle cx="8.75" cy="14.5" r="1.4" />
      <path d="M3.5 14.5h3.4M10.1 14.5h6.4" />
    </Icon>
  );
}

/** Story 16.2's own "Vendors" nav destination — a toolbox glyph, deliberately
 * distinct from `EquipmentIcon`'s own wrench (a vendor is a person/company
 * to call, not a piece of equipment) and from `TenantsIcon`'s own person
 * pair, so the tenth nav slot never reads as a duplicate of an existing one. */
export function VendorsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="8" width="13" height="8" rx="1" />
      <path d="M7.5 8V6.25a2 2 0 0 1 2-2h1a2 2 0 0 1 2 2V8" />
      <path d="M3.5 11.75h13" />
    </Icon>
  );
}

export function MoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="5.25" cy="10" r="1.15" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1.15" fill="currentColor" stroke="none" />
      <circle cx="14.75" cy="10" r="1.15" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/**
 * Story 20.1's own "Menu" nav item (desktop `GlobalTopBar.tsx`/phone
 * `PhoneBottomBar.tsx`) — Owner-requested (2026-09-14) swap from `MoreIcon`'s
 * three dots to a conventional hamburger glyph, which reads more clearly
 * as "opens a menu" than "more options" does. `MoreIcon` itself is
 * untouched and stays in use elsewhere (`RenewalBoardCard.tsx`'s own
 * per-card "more options" trigger is a different, unrelated control).
 */
export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 6h13M3.5 10h13M3.5 14h13" />
    </Icon>
  );
}

/** Story 20.1's nav-bar theme toggle — light mode, click to go dark. */
export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="3.25" />
      <path d="M10 3.25v1.75M10 15v1.75M16.75 10H15M5 10H3.25M14.77 5.23l-1.24 1.24M6.47 13.53l-1.24 1.24M14.77 14.77l-1.24-1.24M6.47 6.47 5.23 5.23" />
    </Icon>
  );
}

/** Story 20.1's nav-bar theme toggle — dark mode, click to go light. */
export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16.25 12.5A6.75 6.75 0 0 1 7.5 3.75a6.75 6.75 0 1 0 8.75 8.75Z" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="8.75" cy="8.75" r="4.75" />
      <path d="M12.25 12.25 16.5 16.5" />
    </Icon>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5.5 13.25V9a4.5 4.5 0 0 1 9 0v4.25l1.25 1.75h-11.5Z" />
      <path d="M8.5 16.5a1.5 1.5 0 0 0 3 0" />
    </Icon>
  );
}

/** Alert severity — "overdue" (Owner-requested, 2026-08-29: a real
 * criticality icon+badge on every alert row, not color alone). */
export function AlertTriangleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10 3.5 17 15.5H3Z" />
      <path d="M10 8.25V11.5" />
      <circle cx="10" cy="13.75" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Alert severity — "needs attention." */
export function AlertCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 7.25V10.5" />
      <circle cx="10" cy="13" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Alert severity — "informational." */
export function InfoCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 9v4" />
      <circle cx="10" cy="6.75" r="0.9" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/**
 * Story 2.3's identifier-column edit affordance (review finding 1): the
 * shared table's first column keeps its real navigation `<Link>` — the
 * whole cell can no longer be a click-to-edit trigger, or the row's own
 * record-page link disappears — so editing that one column is reached
 * through this small, explicit pencil button beside the link instead.
 */
export function PencilIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.9 4.1 15.9 7.1 8.25 14.75H5.25V11.75Z" />
      <path d="M11.4 5.6 14.4 8.6" />
    </Icon>
  );
}

/** Story 3.9's "I've got it" acknowledge action. */
export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 10.5 8.25 14.25 15.5 6" />
    </Icon>
  );
}

/** Story 3.9's "Follow up in…" snooze action. */
export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10.5" r="6.5" />
      <path d="M10 7.25V10.5l3 1.75" />
    </Icon>
  );
}

/** Story 3.9's record-link action (AD-27). */
export function ExternalLinkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8.25 5.5h-2.5A1.75 1.75 0 0 0 4 7.25v7A1.75 1.75 0 0 0 5.75 16h7A1.75 1.75 0 0 0 14.5 14.25v-2.5" />
      <path d="M11.25 4h4.75v4.75" />
      <path d="M16 4 9.5 10.5" />
    </Icon>
  );
}

/** Story 9.2's explicit "Close search" affordance — the search overlay is
 * full-screen on phone, so there is no scrim to tap and Esc has no
 * equivalent one-handed gesture; a visible close control is this codebase's
 * first need for one. */
export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
    </Icon>
  );
}

/** Sign-in's "Private" callout. */
export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="9" width="11" height="7.5" rx="1.25" />
      <path d="M6.5 9V6.5a3.5 3.5 0 0 1 7 0V9" />
    </Icon>
  );
}

/** Sign-in's password-visibility toggle. */
export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.5 10S5.5 4.5 10 4.5 17.5 10 17.5 10 14.5 15.5 10 15.5 2.5 10 2.5 10Z" />
      <circle cx="10" cy="10" r="2.25" />
    </Icon>
  );
}

/** The same glyph, struck through — password currently visible, tap to hide. */
export function EyeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.5 10S5.5 4.5 10 4.5 17.5 10 17.5 10 14.5 15.5 10 15.5 2.5 10 2.5 10Z" />
      <circle cx="10" cy="10" r="2.25" />
      <path d="M4 4l12 12" />
    </Icon>
  );
}

//: Owner-requested (2026-08-28): "the logout function just has a logo with
//: my name so not very clear" — a door-with-an-exiting-arrow glyph next to
//: the "Sign out" menu item itself (`GlobalTopBar.tsx`), the account
//: avatar/initials trigger unchanged (it still opens the account menu, the
//: identical `DropdownMenu` shape the settings menu below now shares).
export function LogOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11.5 3H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h6.5" />
      <path d="M8 10h9M14 6.5 17.5 10 14 13.5" />
    </Icon>
  );
}
